import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Dropdown } from '../../../../models/Dropdown';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil, filter, map, distinctUntilChanged } from 'rxjs/operators';
import { ProgramState } from '../store/program.state';
import { Store } from '@ngxs/store';
import { ProgramActions } from '../store/program.action';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { IProgram } from 'src/app/models/IProgram';
import { ConfirmDialogComponent } from 'src/app/widgets/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ProgramService } from 'src/app/services/aws/program/program.service';
import { ProgramDocumentsComponent } from '../program-documents/program-documents.component';
import { RegistrationComponent } from '../registration/registration.component';
import { SessionsComponent } from '../sessions/sessions.component';
import { ClassesComponent } from '../classes/classes.component';
import { AuthenticationService } from 'src/app/services/authentication/authentication.service';
// Removed OverviewComponent import + ViewChild usage (Overview loads itself based on route params)
import { canComponentExit } from 'src/app/services/guard/un-saved-changes.guard';
import { NavMenuService } from 'src/app/services/nav-menu/nav-menu.service';
@Component({
    selector: 'admin-program',
    templateUrl: './program.component.html',
    styleUrls: ['./program.component.scss'],
    standalone: false
})
export class ProgramComponent implements OnDestroy, OnInit, canComponentExit {
    private destroy$ = new Subject<void>();
    @ViewChild(ProgramDocumentsComponent)
    programDocumentsComponent!: ProgramDocumentsComponent;
    @ViewChild(RegistrationComponent)
    RegistrationComponent!: RegistrationComponent;
    @ViewChild(SessionsComponent)
    sessionsComponent!: SessionsComponent;
    @ViewChild(ClassesComponent)
    classesComponent!: ClassesComponent;
    // Removed (Fix for duplicate API calls + spinner issues)
    // @ViewChild(OverviewComponent)
    // overviewComponent!: OverviewComponent;
    public showGlobalSpinnerParent: boolean = false;
    tabValue = 0;
    breadcrumbdropdowndata: any;
    program: IProgram;
    program$ = this.store.select(ProgramState.programOverview);
    getProgramName$: Observable<any>;
    public isAdminRole: boolean;
    public setTabActive: number;
    breadcrumbOptions;
    tabOptions = {
        tabs: [
            { label: 'program-overview' },
            { label: 'classes' },
            { label: 'sessions' },
            { label: 'registration-status' },
            { label: 'program-documents' }
        ]
    };
    programDocValue: boolean;
    sessionCancelValue: boolean;
    programId: string;
    public email: string = '';
    sessionCount: number = 0;
    programsOverview$ = this.store.select(state => state.programs.programOverview);
    sessionCountReceived(childCompValue: number) {
        this.sessionCount = childCompValue;
    }
    constructor(
        private store: Store,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private dialog: MatDialog,
        private programService: ProgramService,
        private auth: AuthenticationService,
        private navMenuService: NavMenuService
    ) {
        this.isAdminRole = (this.router.url.startsWith('/admin/programs-external') ? false : true);
        this.breadcrumbOptions = {
            breadcrumb: [
                {
                    label: 'Dashboard',
                    itemclass: 'Breadcrumb-item',
                    linkclass: 'Breadcrumb-link',
                    link: this.isAdminRole ? '/admin/dashboard' : '#'
                },
                {
                    label: 'Programs',
                    itemclass: 'Breadcrumb-item',
                    linkclass: 'Breadcrumb-link',
                    link: this.isAdminRole ? '/admin/programs' : '/admin/programs-external'
                }
            ]
            //,
            // dropdowns: [
            //     new Dropdown("Innovative Creative Learning(11/04/2019 - 11/06/2019)", "/admin/programs/1"),
            //     new Dropdown("Business Entertprose Training(11/04/2019 - 11/06/2019)", "/admin/programs/2"),
            //     new Dropdown("SAU Live(11/04/2019 - 11/06/2019)", "/admin/programs/3"),
            //     new Dropdown("Partner Meeting(11/04/2019 - 11/06/2019)", "/admin/programs/4"),
            // ]
        };
        const facilitatorTabOptions = {
            // dropdowns: [],
            tabs: [
                {
                    label: 'Classes',
                    activeTab: 'Classes'
                },
            ]
        };
        this.tabOptions = this.isAdminRole ? this.tabOptions : facilitatorTabOptions;
    }
    canLeaveComponent(nextState = ''): boolean | Observable<boolean> {
        if (this.RegistrationComponent?.shouldBlockTabNavigation()) {
            return this.RegistrationComponent.canLeaveRegistration();
        }
        if (this.classesComponent?.shouldBlockTabNavigation()
            && !this.programService.isProgramModalOpen
            && !this.programService.isSesionsModalOpen) {
            if (this.classesComponent && this.programService.handlingClassSettingBrowserBack) {
                return false;
            }
            return this.classesComponent.openUnsavedChangesDialog().pipe(map((choice) => {
                if (choice) {
                    this.classesComponent.discardChanges();
                    return true;
                }
                if (nextState === '/admin/programs-external') {
                    this.navMenuService.isFacilitatorView();
                    this.navMenuService.disableDropdownMenu$.next('admin');
                }
                return false;
            }));
        }
        if (this.programDocValue || this.sessionCancelValue) {
            return this.openExitDialog(nextState, () => {
                this.programDocValue = false;
                this.sessionCancelValue = false;
            });
        }
        return true;
    }

    private openExitDialog(nextState: string, onConfirm?: () => void): Observable<boolean> {
        return this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: '',
                body: 'Are you sure you want to exit and lose changes?',
                buttonTextNo: 'Keep Editing',
                buttonTextYes: 'Exit'
            },
            panelClass: 'notification-submit-dialog',
            disableClose: true
        }).afterClosed().pipe(map((choice) => {
            if (choice) {
                onConfirm?.();
                return true;
            }
            if (!choice && nextState === '/admin/programs-external') {
                this.navMenuService.isFacilitatorView();
                this.navMenuService.disableDropdownMenu$.next('admin');
            }
            return false;
        }));
    }
    public ngOnInit(): void {
        // When navigation ends, stop global spinner (prevents “Loading…” stuck)
        this.router.events
            .pipe(
                filter(e => e instanceof NavigationEnd),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.showGlobalSpinnerParent = false;
            });
        // Program name: single store subscription (avoid stacking subs on each :type change)
        this.getProgramName$ = this.store.select((state) => state.programs?.programOverview?.name).pipe(
            distinctUntilChanged(),
            takeUntil(this.destroy$)
        );
        // When :type changes, set active tab and program id (URL is source of truth; do not mirror via popstate — duplicates mat-tab events)
        this.activatedRoute.paramMap
            .pipe(takeUntil(this.destroy$))
            .subscribe((data) => {
                this.programId = data.get('id');
                const currentRoute = this.programTabOption(data.get('type'));
                this.setTabActive = currentRoute;
            });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.store.dispatch(new ProgramActions.ClearProgramOverview());
    }
    getProgramDocFormStatus(isFormOpen: boolean) {
        this.programDocValue = isFormOpen;
    }
    getCancelBannerStatus(val: boolean) {
        this.sessionCancelValue = val;
    }
    public navigateToTab(event: any): void {
        const tabFromRoute = this.programTabOption(this.activatedRoute.snapshot.paramMap.get('type'));
        const targetIndex = event?.index ?? 0;
        const classesTabIndex = this.isAdminRole ? 1 : 0;
        if (targetIndex === tabFromRoute) {
            return;
        }
        this.tabValue = targetIndex;
        const registrationTabIndex = 3;
        if (tabFromRoute === registrationTabIndex
            && targetIndex !== registrationTabIndex
            && this.RegistrationComponent?.shouldBlockTabNavigation()) {
            this.RegistrationComponent.canLeaveRegistration().pipe(take(1)).subscribe((confirmed) => {
                if (confirmed) {
                    this.fetchDataOnTabChange(targetIndex);
                }
            });
            this.setTabActive = registrationTabIndex;
            return;
        }
        if (tabFromRoute === classesTabIndex
            && targetIndex !== classesTabIndex
            && this.classesComponent?.shouldBlockTabNavigation()) {
            this.classesComponent.openUnsavedChangesDialog().pipe(take(1)).subscribe((confirmed) => {
                if (confirmed) {
                    this.classesComponent.discardChanges();
                    this.fetchDataOnTabChange(targetIndex);
                }
            });
            this.setTabActive = classesTabIndex;
            return;
        }
        if (this.programDocValue && this.setTabActive !== 4) {
            this.dialog.open(ConfirmDialogComponent, {
                data: {
                    title: "",
                    body: 'Are you sure you want to exit and lose changes?',
                    buttonTextNo: "Keep Editing",
                    buttonTextYes: "Exit"
                },
                panelClass: 'notification-submit-dialog',
                disableClose:true
            }).afterClosed()
                .pipe(take(1))
                .subscribe(result => {
                    if (result) {
                        this.programDocValue = false;
                        this.fetchDataOnTabChange(targetIndex);
                    }
                });
            this.setTabActive = 4;
            return;
        }
        if (this.sessionCancelValue && this.setTabActive !== 2) {
            this.dialog.open(ConfirmDialogComponent, {
                data: {
                    title: "",
                    body: 'Are you sure you want to exit and lose changes?',
                    buttonTextNo: "Keep Editing",
                    buttonTextYes: "Exit"
                },
                panelClass: 'notification-submit-dialog',
                disableClose:true
            }).afterClosed()
                .pipe(take(1))
                .subscribe(result => {
                    if (result) {
                        this.sessionCancelValue = false;
                        this.fetchDataOnTabChange(targetIndex);
                    }
                });
            this.setTabActive = 2;
            return;
        }
        this.fetchDataOnTabChange(targetIndex);
    }
    fetchDataOnTabChange(tabValue: number) {
        this.setTabActive = tabValue;
        this.showGlobalSpinnerParent = true;
        switch (tabValue) {
            case 0:
                // DO NOT call overview ViewChild methods.
                // OverviewComponent loads all APIs on its own using route params (id).
                this.router.navigate([`../program-overview`], { relativeTo: this.activatedRoute });
                break;
            case 1:
                if (this.isAdminRole) {
                    this.classesComponent.getClassesByProgramId(this.programId);
                } else {
                    this.auth.getUserEmail().pipe(take(1)).subscribe(e => {
                        this.email = e.toString();
                        this.classesComponent.getClassesByProgramId(this.programId, this.email);
                    });
                }
                this.router.navigate([`../classes`], { relativeTo: this.activatedRoute });
                break;
            case 2:
                if (!this.sessionCancelValue) {
                    this.sessionsComponent.getClasses(this.programId);
                    this.router.navigate([`../sessions`], { relativeTo: this.activatedRoute });
                }
                break;
            case 3:
                this.RegistrationComponent.getList();
                this.router.navigate([`../registration-status`], { relativeTo: this.activatedRoute });
                break;
            case 4:
                if (!this.programDocValue) {
                    this.programDocumentsComponent.getAllProgramDocs();
                    this.router.navigate([`../program-documents`], { relativeTo: this.activatedRoute });
                }
                break;
            default:
                this.router.navigate([`../program-overview`], { relativeTo: this.activatedRoute });
                break;
        }
    }
    programTabOption(routeName: String): number {
        if (!this.isAdminRole) {
            return 0;
        }
        switch (routeName) {
            case "program-overview": return 0;
            case "classes": return 1;
            case "sessions": return 2;
            case "registration-status": return 3;
            case "program-documents": return 4;
            default: return 0;
        }
    }
}